const MEASUREMENT_PREFERENCE_KEY = "kalpilli_measurement_preference_v2"
const MEASUREMENT_NOTICE_KEY = "kalpilli_measurement_notice_v2"
const MEASUREMENT_ACTIVATION_GUARD_KEY =
  "kalpilli_measurement_activation_guard_v1"
const LEGACY_CONSENT_KEY = "kalpilli_analytics_consent_v1"
const VALID_PREFERENCES = Object.freeze(["enabled", "disabled"])
const ACTIVATION_GUARD_VALUE = "pending"

function readStorageValue(storage, key) {
  if (!storage) return { status: "missing", value: null }
  try {
    const value = storage.getItem(key)
    return value === null
      ? { status: "missing", value: null }
      : { status: "value", value }
  } catch (_) {
    return { status: "error", value: null }
  }
}

function safeGet(storage, key) {
  try {
    return storage ? storage.getItem(key) : null
  } catch (_) {
    return null
  }
}

function safeSet(storage, key, value) {
  try {
    if (!storage) return false
    storage.setItem(key, value)
    return storage.getItem(key) === value
  } catch (_) {
    return false
  }
}

function safeRemove(storage, key) {
  try {
    if (!storage) return false
    storage.removeItem(key)
    return true
  } catch (_) {
    return false
  }
}

function readBrowserLocalStorage(target) {
  try {
    const storage = target ? target.localStorage : null
    return storage
      ? { status: "value", storage }
      : { status: "missing", storage: null }
  } catch (_) {
    return { status: "error", storage: null }
  }
}

function getBrowserLocalStorage(target) {
  return readBrowserLocalStorage(target).storage
}

function isDoNotTrackActive(target = {}) {
  const navigator = target.navigator || {}
  return [navigator.doNotTrack, target.doNotTrack, navigator.msDoNotTrack].some(
    value => value === "1" || String(value).toLowerCase() === "yes"
  )
}

function removeActivationGuard(storage) {
  try {
    if (!storage) return false
    // Web Storage mutations are synchronous and atomic: a non-throwing removal
    // is the commit point. Verify when reads remain available; if a later read
    // is blocked, trust that commit instead of entering an ambiguous rollback.
    storage.removeItem(MEASUREMENT_ACTIVATION_GUARD_KEY)
    return (
      readStorageValue(storage, MEASUREMENT_ACTIVATION_GUARD_KEY).status !==
      "value"
    )
  } catch (_) {
    return false
  }
}

function establishActivationGuard(storage) {
  return safeSet(
    storage,
    MEASUREMENT_ACTIVATION_GUARD_KEY,
    ACTIVATION_GUARD_VALUE
  )
}

function readEffectiveStoredPreference(storage) {
  const current = readStorageValue(storage, MEASUREMENT_PREFERENCE_KEY)
  return current.status === "value" && VALID_PREFERENCES.includes(current.value)
    ? current.value
    : null
}

function failAmbiguousPreferenceWrite(storage) {
  const effectivePreference = readEffectiveStoredPreference(storage)
  if (!effectivePreference) establishActivationGuard(storage)
  return { stored: false, effectivePreference }
}

function storeEnabledPreference(storage) {
  // The guard is a write-ahead record: every interruption before its verified
  // removal is interpreted as disabled by the next snapshot.
  if (!establishActivationGuard(storage)) {
    return { stored: false, effectivePreference: null }
  }

  if (!safeSet(storage, MEASUREMENT_PREFERENCE_KEY, "enabled")) {
    const compensated = safeSet(storage, MEASUREMENT_PREFERENCE_KEY, "disabled")
    if (compensated) {
      removeActivationGuard(storage)
    } else {
      establishActivationGuard(storage)
    }
    return {
      stored: false,
      effectivePreference: compensated ? "disabled" : null,
    }
  }

  if (removeActivationGuard(storage)) {
    const committedPreference = readStorageValue(
      storage,
      MEASUREMENT_PREFERENCE_KEY
    )
    if (committedPreference.status === "error") {
      // removeItem is the synchronous commit point. If storage becomes
      // unreadable only after that operation, preserve the verified commit.
      return { stored: true, effectivePreference: "enabled" }
    }
    if (
      committedPreference.status === "value" &&
      committedPreference.value === "enabled"
    ) {
      return { stored: true, effectivePreference: "enabled" }
    }
    if (
      committedPreference.status === "value" &&
      committedPreference.value === "disabled"
    ) {
      return { stored: false, effectivePreference: "disabled" }
    }
    establishActivationGuard(storage)
    return { stored: false, effectivePreference: null }
  }

  const compensated = safeSet(storage, MEASUREMENT_PREFERENCE_KEY, "disabled")
  if (compensated) {
    removeActivationGuard(storage)
  } else {
    establishActivationGuard(storage)
  }
  return {
    stored: false,
    effectivePreference: compensated ? "disabled" : null,
  }
}

function storeDisabledPreference(storage) {
  // The privacy decision is the first mutation and is independently atomic.
  // The notice is never hidden before `disabled` has been written and read
  // back, so a partial transaction cannot recover as enabled-tacit without a
  // visible notice.
  if (!safeSet(storage, MEASUREMENT_PREFERENCE_KEY, "disabled")) {
    return failAmbiguousPreferenceWrite(storage)
  }

  if (!safeSet(storage, MEASUREMENT_NOTICE_KEY, "dismissed")) {
    return { stored: false, effectivePreference: "disabled" }
  }

  // Read the notice first and the authoritative preference last. If an
  // opposite transaction completes while this function is running, the
  // opt-out cannot report success for an `enabled` final value.
  const notice = readStorageValue(storage, MEASUREMENT_NOTICE_KEY)
  const preference = readStorageValue(storage, MEASUREMENT_PREFERENCE_KEY)
  if (
    preference.status !== "value" ||
    preference.value !== "disabled" ||
    notice.status !== "value" ||
    notice.value !== "dismissed"
  ) {
    if (
      preference.status === "value" &&
      VALID_PREFERENCES.includes(preference.value)
    ) {
      return { stored: false, effectivePreference: preference.value }
    }
    establishActivationGuard(storage)
    return { stored: false, effectivePreference: null }
  }

  return { stored: true, effectivePreference: "disabled" }
}

function storePreferenceAndNotice(preference, storage) {
  if (preference === "disabled") {
    return storeDisabledPreference(storage)
  }

  const previousNotice = safeGet(storage, MEASUREMENT_NOTICE_KEY)
  const noticeWasDismissed = previousNotice === "dismissed"
  if (
    !noticeWasDismissed &&
    !safeSet(storage, MEASUREMENT_NOTICE_KEY, "dismissed")
  ) {
    return { stored: false, effectivePreference: null }
  }
  const transaction = storeEnabledPreference(storage)
  if (transaction.stored) {
    return transaction
  }
  if (!noticeWasDismissed) {
    if (previousNotice === null) {
      safeRemove(storage, MEASUREMENT_NOTICE_KEY)
    } else {
      safeSet(storage, MEASUREMENT_NOTICE_KEY, previousNotice)
    }
  }
  return transaction
}

function failClosedMigration() {
  return {
    result: {
      preference: null,
      noticeDismissed: false,
      migrated: false,
    },
    failClosed: true,
  }
}

function migrateLegacyPreferenceWithStatus(storage) {
  const guard = readStorageValue(storage, MEASUREMENT_ACTIVATION_GUARD_KEY)
  if (guard.status === "error" || guard.status === "value") {
    return failClosedMigration()
  }

  const currentRead = readStorageValue(storage, MEASUREMENT_PREFERENCE_KEY)
  if (currentRead.status === "error") return failClosedMigration()

  const current = currentRead.value
  if (VALID_PREFERENCES.includes(current)) {
    safeRemove(storage, LEGACY_CONSENT_KEY)
    return {
      result: {
        preference: current,
        noticeDismissed:
          safeGet(storage, MEASUREMENT_NOTICE_KEY) === "dismissed",
        migrated: false,
      },
      failClosed: false,
    }
  }

  const legacyRead = readStorageValue(storage, LEGACY_CONSENT_KEY)
  if (legacyRead.status === "error") return failClosedMigration()
  const legacy = legacyRead.value

  const mapped =
    legacy === "accepted"
      ? "enabled"
      : legacy === "rejected"
      ? "disabled"
      : null
  if (!mapped) {
    return {
      result: {
        preference: null,
        noticeDismissed:
          safeGet(storage, MEASUREMENT_NOTICE_KEY) === "dismissed",
        migrated: false,
      },
      failClosed: false,
    }
  }

  const transaction = storePreferenceAndNotice(mapped, storage)
  if (!transaction.stored) {
    const guardAfterFailure = readStorageValue(
      storage,
      MEASUREMENT_ACTIVATION_GUARD_KEY
    )
    const activationFailedClosed =
      mapped === "enabled" && guardAfterFailure.status !== "missing"
    return {
      result: {
        preference: activationFailedClosed
          ? null
          : transaction.effectivePreference || mapped,
        noticeDismissed:
          safeGet(storage, MEASUREMENT_NOTICE_KEY) === "dismissed",
        migrated: false,
      },
      failClosed: activationFailedClosed,
    }
  }
  safeRemove(storage, LEGACY_CONSENT_KEY)
  return {
    result: { preference: mapped, noticeDismissed: true, migrated: true },
    failClosed: false,
  }
}

function migrateLegacyPreference(storage) {
  return migrateLegacyPreferenceWithStatus(storage).result
}

function getMeasurementSnapshot(target = {}, suppliedStorage) {
  const browserStorage =
    suppliedStorage === undefined ? readBrowserLocalStorage(target) : null
  const storage = browserStorage ? browserStorage.storage : suppliedStorage
  const migrationStatus =
    browserStorage && browserStorage.status === "error"
      ? failClosedMigration()
      : migrateLegacyPreferenceWithStatus(storage)
  const migration = migrationStatus.result
  const dntActive = isDoNotTrackActive(target)
  const state = dntActive
    ? "disabled_dnt"
    : migrationStatus.failClosed
    ? "disabled"
    : migration.preference === "disabled"
    ? "disabled"
    : migration.preference === "enabled"
    ? "enabled_explicit"
    : "enabled_tacit"
  return {
    state,
    preference: migration.preference,
    noticeDismissed: migration.noticeDismissed,
    dntActive,
    storage,
  }
}

function isMeasurementEnabled(state) {
  return state === "enabled_tacit" || state === "enabled_explicit"
}

function dismissMeasurementNotice(storage) {
  return safeSet(storage, MEASUREMENT_NOTICE_KEY, "dismissed")
}

function storeMeasurementPreference(preference, storage) {
  if (!VALID_PREFERENCES.includes(preference)) return false
  return storePreferenceAndNotice(preference, storage).stored
}

module.exports = {
  LEGACY_CONSENT_KEY,
  MEASUREMENT_ACTIVATION_GUARD_KEY,
  MEASUREMENT_NOTICE_KEY,
  MEASUREMENT_PREFERENCE_KEY,
  VALID_PREFERENCES,
  dismissMeasurementNotice,
  getBrowserLocalStorage,
  getMeasurementSnapshot,
  isDoNotTrackActive,
  isMeasurementEnabled,
  migrateLegacyPreference,
  safeGet,
  safeRemove,
  safeSet,
  storeMeasurementPreference,
}
